// Task grouping and sorting for calendar display.
// Groups by browser-local YYYY-MM-DD, NOT by raw UTC date substring.

import { localDateString } from "./calendarCodec";

/**
 * Parse a due_date ISO string as a local date key.
 * Returns "YYYY-MM-DD" in browser local time, or null on malformed input.
 */
export function localDayFromDueDate(dueDate) {
  if (!dueDate) return null;
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return null;
  return localDateString(d);
}

/**
 * Group tasks by their browser-local calendar day.
 * Tasks without a valid due_date are excluded.
 * Returns Map<"YYYY-MM-DD", Task[]> sorted by day.
 */
export function groupTasksByLocalDay(tasks) {
  const map = new Map();
  let skippedCount = 0;

  for (const task of tasks || []) {
    const dayKey = localDayFromDueDate(task.due_date);
    if (!dayKey) {
      skippedCount += 1;
      continue;
    }
    if (!map.has(dayKey)) map.set(dayKey, []);
    map.get(dayKey).push(task);
  }

  // Sort tasks within each day: local time asc, created_at asc, id asc
  for (const [, dayTasks] of map) {
    dayTasks.sort((a, b) => {
      const aTime = a.due_date ? new Date(a.due_date).getTime() : 0;
      const bTime = b.due_date ? new Date(b.due_date).getTime() : 0;
      if (aTime !== bTime) return aTime - bTime;
      const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (aCreated !== bCreated) return aCreated - bCreated;
      return (a.id || 0) - (b.id || 0);
    });
  }

  return { byDay: map, skippedCount };
}

export function getTasksForDay(byDay, dateStr) {
  return byDay.get(dateStr) || [];
}

export function formatLocalTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function formatLocalDateTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mn = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mo}-${dd} ${hh}:${mn}`;
}

export function formatLocalDate(dateStr) {
  if (!dateStr) return "";
  // dateStr is "YYYY-MM-DD"
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("uk-UA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function isTaskOverdue(task) {
  if (!task || task.status === "done") return false;
  if (!task.due_date) return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < Date.now();
}
